import { useState, useEffect, useCallback } from "react";
import { Plus, FilePdf, PencilSimple, MinusCircle, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtBRL, fmtDate, todayISO, PAYMENT_METHODS } from "../lib/format";
import { DatePicker } from "./DatePicker";
import { ApplyCreditForm } from "./ApplyCreditForm";
import { ReverseCreditEntry } from "./ReverseCreditEntry";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const input = "bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const emptyCredit = () => ({ date: todayISO(), amount: "", description: "", payment_method: "PIX" });

export const CreditHistoryDialog = ({ client, onClose, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjForm, setAdjForm] = useState({ new_value: "", reason: "" });
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [wdForm, setWdForm] = useState({ amount: "", reason: "" });
  const [form, setForm] = useState(emptyCredit());

  const load = useCallback(() => {
    if (!client) return;
    api.get(`/clients/${client.id}/credits/history`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err)));
  }, [client]);

  useEffect(() => {
    setData(null);
    setForm(emptyCredit());
    setShowAdd(!!client?.openAdd);
    setShowApply(false);
    load();
  }, [client, load]);

  const addCredit = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post(`/clients/${client.id}/credits`, {
        ...form, amount: parseFloat(form.amount),
      });
      toast.success(`Crédito adicionado — saldo de crédito: ${fmtBRL(r.client_credit)}. Lançamento registrado no Financeiro.`);
      setForm(emptyCredit());
      setShowAdd(false);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const adjustCredit = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post(`/clients/${client.id}/credits/adjust`, {
        new_value: parseFloat(adjForm.new_value), reason: adjForm.reason,
      });
      toast.success(`Crédito ajustado para ${fmtBRL(r.client_credit)} (${r.delta > 0 ? "+" : ""}${fmtBRL(r.delta)}) — registrado na auditoria`);
      setAdjForm({ new_value: "", reason: "" });
      setShowAdjust(false);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const withdrawCredit = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post(`/clients/${client.id}/credits/withdraw`, {
        amount: parseFloat(wdForm.amount), reason: wdForm.reason,
      });
      toast.success(`Retirada de ${fmtBRL(r.withdrawn)} registrada — crédito atual: ${fmtBRL(r.client_credit)}`);
      setWdForm({ amount: "", reason: "" });
      setShowWithdraw(false);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/clients/${client.id}/credits/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "extrato_creditos.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Extrato PDF exportado");
    } catch (e) {
      toast.error("Erro ao exportar extrato");
    }
  };

  return (
    <Dialog open={!!client} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Extrato de Créditos — {client?.name?.trim()}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Histórico de quando o crédito entrou e onde foi abatido
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4" data-testid="credit-history-dialog">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="border border-emerald-400/20 bg-emerald-400/5 rounded p-4">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Crédito Atual</p>
                <p data-testid="credit-current" className="font-display text-2xl font-semibold text-emerald-400">{fmtBRL(data.current_credit)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.current_credit > 0 && (
                  <button data-testid="apply-credit-toggle" onClick={() => { setShowApply((v) => !v); setShowAdd(false); setShowAdjust(false); setShowWithdraw(false); }}
                    className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-medium px-3 py-2 rounded transition-colors">
                    <HandCoins size={14} weight="bold" /> Aplicar Crédito
                  </button>
                )}
                <button data-testid="add-credit-toggle" onClick={() => { setShowAdd((v) => !v); setShowAdjust(false); setShowWithdraw(false); setShowApply(false); }}
                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-2 rounded transition-colors">
                  <Plus size={14} weight="bold" /> Adicionar Crédito
                </button>
                <button data-testid="withdraw-credit-toggle" onClick={() => { setShowWithdraw((v) => !v); setShowAdd(false); setShowAdjust(false); setShowApply(false); }}
                  className="flex items-center gap-1.5 border border-rose-400/40 text-rose-400 hover:bg-rose-400/10 text-xs font-medium px-3 py-2 rounded transition-colors">
                  <MinusCircle size={14} /> Remover Crédito
                </button>
                {isAdmin && (
                  <button data-testid="adjust-credit-toggle" onClick={() => { setShowAdjust((v) => !v); setShowAdd(false); setShowWithdraw(false); setShowApply(false); }}
                    className="flex items-center gap-1.5 border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 text-xs font-medium px-3 py-2 rounded transition-colors">
                    <PencilSimple size={14} /> Ajustar (admin)
                  </button>
                )}
                <button data-testid="export-credit-pdf-button" onClick={exportPdf}
                  className="flex items-center gap-1.5 border border-zinc-700 hover:bg-zinc-800 text-zinc-300 text-xs font-medium px-3 py-2 rounded transition-colors">
                  <FilePdf size={14} /> Exportar PDF
                </button>
              </div>
            </div>

            {showApply && data.current_credit > 0 && (
              <ApplyCreditForm clientId={client.id} currentCredit={data.current_credit}
                onApplied={() => { setShowApply(false); load(); onChanged && onChanged(); }} />
            )}

            {showAdd && (
              <form onSubmit={addCredit} data-testid="add-credit-form" className="border border-emerald-400/20 rounded p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Crédito Manual (Adiantamento)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Data</label>
                    <DatePicker testId="credit-date-input" value={form.date}
                      onChange={(v) => setForm({ ...form, date: v })} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Valor (R$)</label>
                    <input data-testid="credit-amount-input" type="number" step="0.01" min="0.01" className={`${input} w-full`}
                      value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Forma de Pagamento</label>
                    <select data-testid="credit-method-select" className={`${input} w-full`} value={form.payment_method}
                      onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                      {PAYMENT_METHODS.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Descrição / Motivo</label>
                  <input data-testid="credit-description-input" className={`${input} w-full`} placeholder="Ex: Adiantamento para próximo pedido"
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <button data-testid="save-credit-button"
                  className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium py-2.5 rounded transition-colors">
                  <Plus size={15} weight="bold" /> Registrar Crédito
                </button>
                <p className="text-[11px] text-zinc-500">O valor entrará como recebimento no Fluxo de Caixa e ficará disponível como crédito para abater em pedidos futuros.</p>
              </form>
            )}

            {showWithdraw && (
              <form onSubmit={withdrawCredit} data-testid="withdraw-credit-form" className="border border-rose-400/30 rounded p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.15em] text-rose-400">Retirada Manual de Crédito — auditada</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Valor a retirar (R$)</label>
                    <input data-testid="withdraw-amount-input" type="number" step="0.01" min="0.01" className={`${input} w-full`}
                      placeholder={`Disponível: ${fmtBRL(data.current_credit)}`}
                      value={wdForm.amount} onChange={(e) => setWdForm({ ...wdForm, amount: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Motivo (opcional)</label>
                    <input data-testid="withdraw-reason-input" className={`${input} w-full`} placeholder="Ex: devolução em dinheiro ao cliente"
                      value={wdForm.reason} onChange={(e) => setWdForm({ ...wdForm, reason: e.target.value })} />
                  </div>
                </div>
                <button data-testid="save-withdraw-button"
                  className="w-full flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium py-2.5 rounded transition-colors">
                  <MinusCircle size={15} weight="bold" /> Registrar Retirada
                </button>
                <p className="text-[11px] text-zinc-500">Gera um lançamento negativo "Retirada manual de crédito" no extrato, sem apagar o histórico — fica registrado quanto havia, quando foi retirado e por quem.</p>
              </form>
            )}

            {showAdjust && isAdmin && (
              <form onSubmit={adjustCredit} data-testid="adjust-credit-form" className="border border-amber-400/30 rounded p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.15em] text-amber-400">Ajuste de Crédito (Admin) — auditado</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Novo valor do crédito (R$)</label>
                    <input data-testid="adjust-value-input" type="number" step="0.01" min="0" className={`${input} w-full`}
                      placeholder={`Atual: ${fmtBRL(data.current_credit)}`}
                      value={adjForm.new_value} onChange={(e) => setAdjForm({ ...adjForm, new_value: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 block mb-1">Motivo do ajuste (obrigatório)</label>
                    <input data-testid="adjust-reason-input" className={`${input} w-full`} placeholder="Ex: correção de lançamento duplicado"
                      value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} required />
                  </div>
                </div>
                <button data-testid="save-adjust-button"
                  className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium py-2.5 rounded transition-colors">
                  <PencilSimple size={15} weight="bold" /> Aplicar Ajuste
                </button>
                <p className="text-[11px] text-zinc-500">Cria um lançamento de ajuste no extrato (positivo ou negativo) sem apagar nada — o motivo, o valor anterior e quem fez ficam gravados na auditoria.</p>
              </form>
            )}

            <div className="border border-zinc-800 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-zinc-500 border-b border-zinc-800">
                    <th className="px-4 py-2.5 font-medium">Data</th>
                    <th className="px-4 py-2.5 font-medium">Descrição</th>
                    <th className="px-4 py-2.5 font-medium">Forma</th>
                    <th className="px-4 py-2.5 font-medium text-right">Valor</th>
                    <th className="px-4 py-2.5 font-medium text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-500">Nenhuma movimentação de crédito ainda.</td></tr>
                  )}
                  {data.entries.map((e) => (
                    <tr key={e.id} data-testid={`credit-entry-${e.id}`} className="border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors">
                      <td className="px-4 py-2.5 text-zinc-400 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-2.5 text-zinc-300">
                        {e.description}
                        {e.created_by && <span className="block text-[10px] text-zinc-500">por {e.created_by}</span>}
                        {e.reversed_at && (
                          <span data-testid={`credit-entry-reversed-${e.id}`} className="inline-block mt-1 text-[10px] uppercase tracking-[0.1em] text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">
                            Estornado{e.reversed_by ? ` por ${e.reversed_by}` : ""}
                          </span>
                        )}
                        {isAdmin && e.can_reverse && (
                          <ReverseCreditEntry clientId={client.id} entry={e}
                            onReversed={() => { load(); onChanged && onChanged(); }} />
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-400 whitespace-nowrap" data-testid={`credit-entry-method-${e.id}`}>
                        {e.payment_method || "—"}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-medium ${e.amount > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {e.amount > 0 ? "+" : ""}{fmtBRL(e.amount)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-zinc-400">{fmtBRL(e.balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
